import React, { useState, useMemo } from 'react';
import Title from 'antd/es/typography/Title';
import { Button, Input, Space, Table, Modal, Alert, Spin, Card, DatePicker, TimePicker } from 'antd';
import { UserOutlined, MobileOutlined, HeartOutlined, HomeOutlined } from '@ant-design/icons';
import { RefActiveParticipantsWithAllPlacements, refreshAllDatasets } from '../hooks/DatasetHooks';
import * as libCon from '../community-hats-js-library/Constants.js';
import LoadingDatasets from '../components/LoadingDatasets';
import { endPlacement } from '../community-hats-js-library/back-end/router';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { RefContextLanguage } from '../providers/LanguageProvider';
import { tValue } from '../utils/translationFunctions';

dayjs.extend(utc);
dayjs.extend(timezone);

// Current time in the study's timezone
const getStudyLocationTime = () => dayjs().tz(libCon.GENERAL_CONFIG.timezone);

const getParticipantColumns = (i18n) => [
  {
    title: i18n.t('sewaId'),
    dataIndex: libCon.ATF_SEWA_ID,
    sorter: (a, b) => (a[libCon.ATF_SEWA_ID] || '').localeCompare(b[libCon.ATF_SEWA_ID] || ''),
    align: 'center',
    width: 120,
  },
  {
    title: i18n.t('name'),
    dataIndex: libCon.ATF_NAME,
    sorter: (a, b) => (a[libCon.ATF_NAME] || '').localeCompare(b[libCon.ATF_NAME] || ''),
    align: 'center',
    width: 200,
  },
  {
    title: i18n.t('phone'),
    dataIndex: libCon.ATF_HAS_ACTIVE_PHONES,
    render: (val) => tValue(i18n, val ? libCon.YES : libCon.NO),
    align: 'center',
    width: 80,
  },
  {
    title: i18n.t('wearable'),
    dataIndex: libCon.ATF_HAS_ACTIVE_WEARABLES,
    render: (val) => tValue(i18n, val ? libCon.YES : libCon.NO),
    align: 'center',
    width: 80,
  },
  {
    title: i18n.t('sensors'),
    dataIndex: libCon.ATF_TOTAL_ACTIVE_SENSORS,
    align: 'center',
    width: 80,
  },
];

function ParticipantUnenroll() {
  const [searchText, setSearchText] = useState('');
  const [selectedParticipant, setSelectedParticipant] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [unenrollStatus, setUnenrollStatus] = useState(null);
  const [unenrollMessages, setUnenrollMessages] = useState([]);
  const [isUnenrolling, setIsUnenrolling] = useState(false);
  const [endDate, setEndDate] = useState(() => dayjs());
  const [endTime, setEndTime] = useState(() => dayjs());

  const i18n = RefContextLanguage();

  const [participantStatus, participants] = RefActiveParticipantsWithAllPlacements();

  const filteredParticipants = useMemo(() => {
    if (!participants || !searchText.trim()) return [];
    const search = searchText.toLowerCase().trim();
    return Object.values(participants).filter((p) => {
      const name = (p[libCon.ATF_NAME] || '').toLowerCase();
      const sewaId = (p[libCon.ATF_SEWA_ID] || '').toLowerCase();
      return name.includes(search) || sewaId.includes(search);
    });
  }, [participants, searchText]);

  const columns = [
    ...getParticipantColumns(i18n),
    {
      title: i18n.t('action'),
      key: 'action',
      align: 'center',
      width: 100,
      render: (_, record) => (
        <Button
          type="primary"
          size="small"
          onClick={() => {
            setSelectedParticipant(record);
            setUnenrollStatus(null);
          }}
        >
          {i18n.t('select')}
        </Button>
      ),
    },
  ];

  const handleUnenroll = async () => {
    if (!selectedParticipant) return;

    setIsUnenrolling(true);
    setUnenrollMessages([]);
    const messages = [];
    let hasError = false;

    // Format the end date/time as yyyy-MM-dd HH:mm:ss
    const formattedEndDate = `${endDate.format('YYYY-MM-DD')} ${endTime.format('HH:mm')}:00`;

    // End participant placements
    const participantPlacements = selectedParticipant[libCon.ATF_PARTICIPANT_PLACEMENT] || [];
    const activePP = participantPlacements.filter((p) => p[libCon.ATF_ACTIVE] === libCon.YES);
    for (const placement of activePP) {
      const [status] = await endPlacement(
        libCon.AIRTABLE_CONFIG[libCon.AT_TABLE_PARTICIPANTS_PLACEMENT],
        placement[libCon.ID],
        formattedEndDate
      );
      if (status === libCon.OK) {
        messages.push(i18n.t('participantPlacementEndedSuccessfully'));
      } else {
        messages.push(i18n.t('failedToEndParticipantPlacement'));
        hasError = true;
      }
    }

    // End phone placements
    const phonePlacements = selectedParticipant[libCon.ATF_PHONE_PLACEMENTS] || [];
    const activePhoP = phonePlacements.filter((p) => p[libCon.ATF_ACTIVE] === libCon.YES);
    for (const placement of activePhoP) {
      const [status] = await endPlacement(
        libCon.AIRTABLE_CONFIG[libCon.AT_TABLE_PHONE_PLACEMENTS],
        placement[libCon.ID],
        formattedEndDate
      );
      if (status === libCon.OK) {
        messages.push(i18n.t('phonePlacementEndedSuccessfully'));
      } else {
        messages.push(i18n.t('failedToEndPhonePlacement'));
        hasError = true;
      }
    }

    // End wearable placements
    const wearablePlacements = selectedParticipant[libCon.ATF_WEARABLE_PLACEMENT] || [];
    const activeWP = wearablePlacements.filter((p) => p[libCon.ATF_ACTIVE] === libCon.YES);
    for (const placement of activeWP) {
      const [status] = await endPlacement(
        libCon.AIRTABLE_CONFIG[libCon.AT_TABLE_WEARABLE_PLACEMENTS],
        placement[libCon.ID],
        formattedEndDate
      );
      if (status === libCon.OK) {
        messages.push(i18n.t('wearablePlacementEndedSuccessfully'));
      } else {
        messages.push(i18n.t('failedToEndWearablePlacement'));
        hasError = true;
      }
    }

    // End sensor placements
    const sensorPlacements = selectedParticipant[libCon.ATF_SENSOR_PLACEMENTS] || [];
    const activeSP = sensorPlacements.filter((p) => p[libCon.ATF_ACTIVE] === libCon.YES);
    for (const placement of activeSP) {
      const [status] = await endPlacement(
        libCon.AIRTABLE_CONFIG[libCon.AT_TABLE_SENSOR_HOUSE_PLACEMENT],
        placement[libCon.ID],
        formattedEndDate
      );
      if (status === libCon.OK) {
        messages.push(i18n.t('sensorPlacementEndedSuccessfully'));
      } else {
        messages.push(i18n.t('failedToEndSensorPlacement'));
        hasError = true;
      }
    }

    if (activePP.length === 0 && activePhoP.length === 0 && activeWP.length === 0 && activeSP.length === 0) {
      messages.push(i18n.t('noActivePlacementsFound'));
    }

    setUnenrollMessages(messages);
    setUnenrollStatus(hasError ? 'error' : 'success');
    setIsUnenrolling(false);
    setIsModalOpen(false);

    // Update selectedParticipant to reflect ended placements
    if (!hasError) {
      const updatedParticipant = { ...selectedParticipant };
      // Mark all placements as inactive
      if (updatedParticipant[libCon.ATF_PARTICIPANT_PLACEMENT]) {
        updatedParticipant[libCon.ATF_PARTICIPANT_PLACEMENT] = updatedParticipant[libCon.ATF_PARTICIPANT_PLACEMENT].map(
          (p) => ({ ...p, [libCon.ATF_ACTIVE]: libCon.NO })
        );
      }
      if (updatedParticipant[libCon.ATF_PHONE_PLACEMENTS]) {
        updatedParticipant[libCon.ATF_PHONE_PLACEMENTS] = updatedParticipant[libCon.ATF_PHONE_PLACEMENTS].map((p) => ({
          ...p,
          [libCon.ATF_ACTIVE]: libCon.NO,
        }));
      }
      if (updatedParticipant[libCon.ATF_WEARABLE_PLACEMENT]) {
        updatedParticipant[libCon.ATF_WEARABLE_PLACEMENT] = updatedParticipant[libCon.ATF_WEARABLE_PLACEMENT].map(
          (p) => ({ ...p, [libCon.ATF_ACTIVE]: libCon.NO })
        );
      }
      if (updatedParticipant[libCon.ATF_SENSOR_PLACEMENTS]) {
        updatedParticipant[libCon.ATF_SENSOR_PLACEMENTS] = updatedParticipant[libCon.ATF_SENSOR_PLACEMENTS].map(
          (p) => ({
            ...p,
            [libCon.ATF_ACTIVE]: libCon.NO,
          })
        );
      }
      setSelectedParticipant(updatedParticipant);
    }
  };

  const getActivePlacementsSummary = () => {
    if (!selectedParticipant) return '';
    const parts = [];
    const pp = (selectedParticipant[libCon.ATF_PARTICIPANT_PLACEMENT] || []).filter(
      (p) => p[libCon.ATF_ACTIVE] === libCon.YES
    );
    const phoP = (selectedParticipant[libCon.ATF_PHONE_PLACEMENTS] || []).filter(
      (p) => p[libCon.ATF_ACTIVE] === libCon.YES
    );
    const wp = (selectedParticipant[libCon.ATF_WEARABLE_PLACEMENT] || []).filter(
      (p) => p[libCon.ATF_ACTIVE] === libCon.YES
    );
    const sp = (selectedParticipant[libCon.ATF_SENSOR_PLACEMENTS] || []).filter(
      (p) => p[libCon.ATF_ACTIVE] === libCon.YES
    );

    if (pp.length > 0) parts.push(`${pp.length} ${i18n.t('participantPlacements')}`);
    if (phoP.length > 0) parts.push(`${phoP.length} ${i18n.t('phonePlacements')}`);
    if (wp.length > 0) parts.push(`${wp.length} ${i18n.t('wearablePlacements')}`);
    if (sp.length > 0) parts.push(`${sp.length} ${i18n.t('sensorPlacements')}`);

    return parts.length > 0 ? parts.join(', ') : i18n.t('noActivePlacements');
  };

  const getActivePlacementCounts = () => {
    if (!selectedParticipant) return { pp: 0, phone: 0, wearable: 0, sensor: 0 };
    const pp = (selectedParticipant[libCon.ATF_PARTICIPANT_PLACEMENT] || []).filter(
      (p) => p[libCon.ATF_ACTIVE] === libCon.YES
    ).length;
    const phone = (selectedParticipant[libCon.ATF_PHONE_PLACEMENTS] || []).filter(
      (p) => p[libCon.ATF_ACTIVE] === libCon.YES
    ).length;
    const wearable = (selectedParticipant[libCon.ATF_WEARABLE_PLACEMENT] || []).filter(
      (p) => p[libCon.ATF_ACTIVE] === libCon.YES
    ).length;
    const sensor = (selectedParticipant[libCon.ATF_SENSOR_PLACEMENTS] || []).filter(
      (p) => p[libCon.ATF_ACTIVE] === libCon.YES
    ).length;
    return { pp, phone, wearable, sensor };
  };

  return (
    <div className="mainContainer">
      <Title level={3}>{i18n.t('participantUnenroll')}</Title>

      {participantStatus !== libCon.OK ? (
        <LoadingDatasets />
      ) : (
        <div>
          <Space direction="horizontal" style={{ marginBottom: '20px' }}>
            <Button type="primary" onClick={() => refreshAllDatasets()}>
              {i18n.t('refreshAll')}
            </Button>
            <Input
              placeholder={i18n.t('searchByNameOrSewaId')}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: 300 }}
            />
            <Button
              onClick={() => {
                setSearchText('');
                setSelectedParticipant(null);
                setUnenrollStatus(null);
              }}
            >
              {i18n.t('clear')}
            </Button>
          </Space>

          {searchText.trim() && (
            <div style={{ maxWidth: '98vw', marginBottom: '20px' }}>
              <Table
                dataSource={filteredParticipants}
                columns={columns}
                size="small"
                rowKey={libCon.ID}
                scroll={{ x: 'max-content', y: window.innerHeight * 0.3 }}
                pagination={{ pageSize: 10 }}
              />
            </div>
          )}

          <Space direction="horizontal">
            {selectedParticipant && (
              <div className="pageContainer">
                <Card
                  title={i18n.t('selectedParticipant')}
                  style={{
                    border: '4px solid var(--primary-color-6)',
                  }}
                  headStyle={{
                    backgroundColor: 'var(--primary-color-6-transparent)',
                  }}
                >
                  <p>
                    <strong>{i18n.t('name')}:</strong> {selectedParticipant[libCon.ATF_NAME]}
                  </p>
                  <p>
                    <strong>{i18n.t('sewaId')}:</strong> {selectedParticipant[libCon.ATF_SEWA_ID]}
                  </p>

                  <div style={{ marginTop: '15px', marginBottom: '15px' }}>
                    <strong>{i18n.t('activePlacements')}:</strong>
                    <Space
                      size="large"
                      style={{ marginLeft: '10px', marginTop: '10px', display: 'flex', flexWrap: 'wrap' }}
                    >
                      <span title={i18n.t('participantPlacement')}>
                        <UserOutlined
                          style={{
                            fontSize: '24px',
                            color:
                              getActivePlacementCounts().pp > 0 ? 'var(--success-color-1)' : 'var(--warning-color-1)',
                          }}
                        />
                        <span style={{ marginLeft: '5px' }}>{getActivePlacementCounts().pp}</span>
                      </span>
                      <span title={i18n.t('phonePlacement')}>
                        <MobileOutlined
                          style={{
                            fontSize: '24px',
                            color:
                              getActivePlacementCounts().phone > 0
                                ? 'var(--success-color-1)'
                                : 'var(--warning-color-1)',
                          }}
                        />
                        <span style={{ marginLeft: '5px' }}>{getActivePlacementCounts().phone}</span>
                      </span>
                      <span title={i18n.t('wearablePlacement')}>
                        <HeartOutlined
                          style={{
                            fontSize: '24px',
                            color:
                              getActivePlacementCounts().wearable > 0
                                ? 'var(--success-color-1)'
                                : 'var(--warning-color-1)',
                          }}
                        />
                        <span style={{ marginLeft: '5px' }}>{getActivePlacementCounts().wearable}</span>
                      </span>
                      <span title={i18n.t('sensorPlacementsTitle')}>
                        <HomeOutlined
                          style={{
                            fontSize: '24px',
                            color:
                              getActivePlacementCounts().sensor > 0
                                ? 'var(--success-color-1)'
                                : 'var(--warning-color-1)',
                          }}
                        />
                        <span style={{ marginLeft: '5px' }}>{getActivePlacementCounts().sensor}</span>
                      </span>
                    </Space>
                  </div>

                  <Space style={{ marginTop: '15px' }}>
                    <Button
                      danger
                      type="primary"
                      onClick={() => {
                        const studyLocationTime = getStudyLocationTime();
                        setEndDate(studyLocationTime);
                        setEndTime(studyLocationTime);
                        setIsModalOpen(true);
                      }}
                      disabled={isUnenrolling}
                    >
                      {i18n.t('endAllPlacements')}
                    </Button>
                    <Button
                      onClick={() => {
                        setSelectedParticipant(null);
                        setUnenrollStatus(null);
                        setUnenrollMessages([]);
                      }}
                    >
                      {i18n.t('clearSelection')}
                    </Button>
                  </Space>
                </Card>
              </div>
            )}
          </Space>

          {selectedParticipant && unenrollStatus && (
            <div style={{ marginTop: '20px' }}>
              <Alert
                type={unenrollStatus}
                message={unenrollStatus === 'success' ? i18n.t('unenrollCompleted') : i18n.t('unenrollCompletedWithErrors')}
                description={
                  <ul style={{ margin: 0, paddingLeft: '20px' }}>
                    {unenrollMessages.map((msg, idx) => (
                      <li key={idx}>{msg}</li>
                    ))}
                  </ul>
                }
                showIcon
              />
            </div>
          )}

          <Modal
            title={i18n.t('confirmUnenroll')}
            open={isModalOpen}
            onOk={handleUnenroll}
            onCancel={() => setIsModalOpen(false)}
            okText={i18n.t('yesEndAllPlacements')}
            okButtonProps={{ danger: true, loading: isUnenrolling }}
            cancelButtonProps={{ disabled: isUnenrolling }}
          >
            {isUnenrolling ? (
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <Spin />
                <p>{i18n.t('endingPlacements')}</p>
              </div>
            ) : (
              <div>
                <p>
                  {i18n.t('areYouSureEndAllPlacements')}{' '}
                  <strong>{selectedParticipant?.[libCon.ATF_NAME]}</strong>?
                </p>
                <p>
                  {i18n.t('thisWillEnd')}: {getActivePlacementsSummary()}
                </p>
                <div style={{ marginTop: '15px', marginBottom: '15px' }}>
                  <p style={{ marginBottom: '5px' }}>
                    <strong>{i18n.t('endDateStudyTime', { timezone: libCon.GENERAL_CONFIG.timezone })}:</strong>
                  </p>
                  <Space direction="horizontal">
                    <DatePicker value={endDate} onChange={(date) => date && setEndDate(date)} />
                    <TimePicker value={endTime} onChange={(time) => time && setEndTime(time)} format="HH:mm" />
                  </Space>
                </div>
                <p style={{ color: '#ff4d4f' }}>{i18n.t('thisActionCannotBeUndone')}</p>
              </div>
            )}
          </Modal>
        </div>
      )}
    </div>
  );
}

export default ParticipantUnenroll;
